import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NodeSelection } from "@tiptap/pm/state";
import TiptapEditor from "./TiptapEditor";
import TiptapToolbar from "./TiptapToolbar";
import TiptapHtmlToggle from "./TiptapHtmlToggle";
import { DEFAULT_RESIZABLE_IMAGE_WIDTH } from "./extensions/resizableImageExtension";
import { uploadImageToCloudinary } from "../../../utils/cloudinaryUpload";
import { normalizeFormulasForPersistence } from "../../../utils/renderSavedFormulas";
import "./styles/text-editor.css";
import "../../../styles/LessonContentTypography.css";

const COLORS = [
  { label: "Schwarz", value: "#000000" },
  { label: "Blau", value: "#0b1c30" },
  { label: "Teal", value: "#10A6A9" },
  { label: "Orange", value: "#E76F51" },
  { label: "Gruen", value: "#2f855a" },
  { label: "Grau", value: "#64748b" },
  { label: "Rot", value: "#dc2626" },
];

const MAX_IMAGE_BYTES6 = 15 * 1024 * 1024;

function parseStyledBundle(styledHtml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${styledHtml || ""}</div>`, "text/html");
  const styleEl = doc.querySelector("style");
  const contentDiv = doc.querySelector("div.learnhut-content");

  const css = styleEl?.textContent || "";
  const contentInner = contentDiv ? contentDiv.innerHTML : "";
  const hasBundle = !!contentDiv;
  return { css, contentInner, hasBundle };
}

function buildPersistedHtmlFromInner(inner, cssText) {
  if (cssText && cssText.trim()) {
    return `<style>${cssText}</style><div class="learnhut-content">${inner || "<p></p>"}</div>`;
  }
  return inner;
}

function normalizeImagesForPersistence(innerHtml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${innerHtml || ""}</div>`, "text/html");
  const wrapper = doc.body.firstElementChild;
  if (!wrapper) return innerHtml || "";

  wrapper.querySelectorAll("img").forEach((img) => {
    const src = (img.getAttribute("src") || "").trim();
    if (!src) {
      img.remove();
      return;
    }

    const rawWidth = img.getAttribute("width") || img.getAttribute("data-width") || img.style?.width || "";
    const width = parseInt(String(rawWidth).replace("px", ""), 10);

    const rawLayout =
      img.getAttribute("data-lh-image-layout") ||
      (img.classList?.contains("lh6-image-align-left")
        ? "left"
        : img.classList?.contains("lh6-image-align-right")
        ? "right"
        : "center");
    const layout = ["left", "right", "center"].includes(rawLayout) ? rawLayout : "center";

    img.removeAttribute("data-lh6-image");
    img.removeAttribute("data-width");
    img.removeAttribute("style");
    img.classList.remove("lh6-image-align-left", "lh6-image-align-right", "lh6-image-align-center");
    img.classList.add(`lh6-image-align-${layout}`);
    img.setAttribute("data-lh-image-layout", layout);

    if (Number.isFinite(width) && width > 0) {
      img.setAttribute("width", String(width));
    } else {
      img.setAttribute("width", String(DEFAULT_RESIZABLE_IMAGE_WIDTH));
    }
  });

  return wrapper.innerHTML;
}

function escapeHtml6(str = "") {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

const VOID_TAGS6 = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

function getTagName6(token = "") {
  const m = token.match(/^<\s*\/?\s*([a-zA-Z0-9:-]+)/);
  return (m?.[1] || "").toLowerCase();
}

function formatHtmlSource6(rawHtml) {
  const source = (rawHtml || "").replace(/\r\n?/g, "\n").trim();
  if (!source) return "";

  const tokens = source.match(/<!--[\s\S]*?-->|<\/?[^>]+>|[^<]+/g);
  if (!tokens || tokens.length === 0) return source;

  const lines = [];
  let depth = 0;
  const pad = () => "  ".repeat(Math.max(0, depth));

  for (const tokenRaw of tokens) {
    const token = tokenRaw.trim();
    if (!token) continue;

    if (!token.startsWith("<")) {
      const text = token.replace(/\s+/g, " ").trim();
      if (text) lines.push(`${pad()}${text}`);
      continue;
    }

    if (token.startsWith("<!--")) {
      lines.push(`${pad()}${token}`);
      continue;
    }

    const isClosing = /^<\s*\//.test(token);
    const isSelfClosing = /\/\s*>$/.test(token);
    const tagName = getTagName6(token);

    if (isClosing) {
      depth = Math.max(0, depth - 1);
      lines.push(`${pad()}${token}`);
      continue;
    }

    lines.push(`${pad()}${token}`);
    if (tagName && !isSelfClosing && !VOID_TAGS6.has(tagName)) {
      depth += 1;
    }
  }

  return lines.join("\n");
}

function highlightTagToken6(token) {
  const closing = token.match(/^<\s*\/\s*([^\s>]+)\s*>$/);
  if (closing) {
    return `<span class="lh6-codeBracket">&lt;/</span><span class="lh6-codeTag">${escapeHtml6(
      closing[1]
    )}</span><span class="lh6-codeBracket">&gt;</span>`;
  }

  const opening = token.match(/^<\s*([^\s/>]+)([\s\S]*?)\s*(\/?)>$/);
  if (!opening) {
    return `<span class="lh6-codeTagRaw">${escapeHtml6(token)}</span>`;
  }

  const [, tagName, attrsRaw, slash] = opening;
  const attrs = [];
  const attrRe = /([^\s=/]+)(?:\s*=\s*(".*?"|'.*?'|[^\s"'>=]+))?/g;
  let match;
  while ((match = attrRe.exec(attrsRaw))) {
    const [, name, value] = match;
    if (!name) continue;
    if (typeof value === "string") {
      attrs.push(
        `<span class="lh6-codeAttr">${escapeHtml6(name)}</span><span class="lh6-codeOp">=</span><span class="lh6-codeValue">${escapeHtml6(
          value
        )}</span>`
      );
    } else {
      attrs.push(`<span class="lh6-codeAttr">${escapeHtml6(name)}</span>`);
    }
  }

  const attrsJoined = attrs.length ? ` ${attrs.join(" ")}` : "";
  const closingPart = slash ? '<span class="lh6-codeBracket">/</span>' : "";

  return `<span class="lh6-codeBracket">&lt;</span><span class="lh6-codeTag">${escapeHtml6(
    tagName
  )}</span>${attrsJoined}${closingPart}<span class="lh6-codeBracket">&gt;</span>`;
}

function highlightHtmlSource6(rawHtml) {
  const src = rawHtml || "";
  if (!src) return "";

  const tokens = src.match(/<!--[\s\S]*?-->|<\/?[^>]+>|[^<]+/g) || [];
  return tokens
    .map((token) => {
      if (token.startsWith("<!--")) {
        return `<span class="lh6-codeComment">${escapeHtml6(token)}</span>`;
      }
      if (token.startsWith("<!")) {
        return `<span class="lh6-codeDoctype">${escapeHtml6(token)}</span>`;
      }
      if (token.startsWith("<")) {
        return highlightTagToken6(token);
      }
      return `<span class="lh6-codeText">${escapeHtml6(token)}</span>`;
    })
    .join("");
}

function parseFontSizePx(fontSize, fallback = 13) {
  if (!fontSize) return fallback;
  const parsed = parseInt(String(fontSize).replace("px", ""), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getSelectedFormula(editor) {
  const selection = editor?.state?.selection;
  if (!(selection instanceof NodeSelection)) return null;
  if (selection.node.type.name !== "formula") return null;

  return {
    pos: selection.from,
    latex: selection.node.attrs?.latex || "",
    displayMode: !!selection.node.attrs?.displayMode,
  };
}

function getSelectedImage(editor) {
  const selection = editor?.state?.selection;
  if (!(selection instanceof NodeSelection)) return null;
  if (selection.node.type.name !== "resizableImage") return null;

  return {
    pos: selection.from,
    attrs: selection.node.attrs || {},
  };
}

function safeFocusTextarea(ref) {
  try {
    ref?.current?.focus();
  } catch {}
}

export default function TextEditorOrchestrator({
  open,
  initialHtml = "",
  mode = "create",
  title,
  subtitle = "Learnhut Text Editor",
  onSave,
  onRequestClose,
  onDirtyChange,
}) {
  const htmlRef = useRef(null);
  const htmlPreviewRef = useRef(null);
  const imageInputRef = useRef(null);
  const editorRef = useRef(null);
  const htmlModeRef = useRef(false);
  const syncingEditorRef = useRef(false);
  const latestHtmlCodeRef = useRef("");

  const [editorReadyVersion, setEditorReadyVersion] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlCode, setHtmlCode] = useState("");

  const [format, setFormat] = useState("p");
  const [color, setColor] = useState(COLORS[0].value);
  const [fontSize, setFontSize] = useState(13);
  const [activeMarks, setActiveMarks] = useState({
    bold: false,
    italic: false,
    bulletList: false,
    orderedList: false,
    superscript: false,
    subscript: false,
    link: false,
    greyBackground: false,
  });
  const [imageSelected, setImageSelected] = useState(false);
  const [selectedImageLayout, setSelectedImageLayout] = useState("center");

  const [formulaSelected, setFormulaSelected] = useState(false);

  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    htmlModeRef.current = htmlMode;
  }, [htmlMode]);


  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (open) return;
    setFormulaSelected(false);
  }, [open]);

  useEffect(() => {
    if (!htmlMode) return;
    const pre = htmlPreviewRef.current;
    const ta = htmlRef.current;
    if (!pre || !ta) return;
    pre.scrollTop = ta.scrollTop;
    pre.scrollLeft = ta.scrollLeft;
  }, [htmlMode, htmlCode]);


  const markDirty = useCallback(() => {
    setDirty((prev) => (prev ? prev : true));
  }, []);

  const syncToolbarState = useCallback((editor) => {
    if (!editor) return;

    const nextFormat = editor.isActive("heading", { level: 1 })
      ? "h1"
      : editor.isActive("heading", { level: 2 })
      ? "h2"
      : editor.isActive("heading", { level: 3 })
      ? "h3"
      : editor.isActive("heading", { level: 4 })
      ? "h4"
      : "p";

    const textStyleAttrs = editor.getAttributes("textStyle") || {};
    const nextColor = textStyleAttrs.color || COLORS[0].value;
    const nextFontSize = parseFontSizePx(textStyleAttrs.fontSize, 13);

    setFormat((prev) => (prev === nextFormat ? prev : nextFormat));
    setColor((prev) => (prev === nextColor ? prev : nextColor));
    setFontSize((prev) => (prev === nextFontSize ? prev : nextFontSize));

    const nextActive = {
      bold: editor.isActive("bold"),
      italic: editor.isActive("italic"),
      bulletList: editor.isActive("bulletList"),
      orderedList: editor.isActive("orderedList"),
      superscript: editor.isActive("superscript"),
      subscript: editor.isActive("subscript"),
      link: editor.isActive("link"),
      greyBackground: editor.isActive("greyBackground"),
    };

    setActiveMarks((prev) => {
      if (
        prev.bold === nextActive.bold &&
        prev.italic === nextActive.italic &&
        prev.bulletList === nextActive.bulletList &&
        prev.orderedList === nextActive.orderedList &&
        prev.superscript === nextActive.superscript &&
        prev.subscript === nextActive.subscript &&
        prev.link === nextActive.link &&
        prev.greyBackground === nextActive.greyBackground
      ) {
        return prev;
      }
      return nextActive;
    });

    const selectedFormula = getSelectedFormula(editor);
    const selectedImage = getSelectedImage(editor);
    const nextImageLayout = selectedImage?.attrs?.layout || "center";

    setImageSelected((prev) => (prev === !!selectedImage ? prev : !!selectedImage));
    setSelectedImageLayout((prev) => (prev === nextImageLayout ? prev : nextImageLayout));
    setFormulaSelected((prev) => (prev === !!selectedFormula ? prev : !!selectedFormula));
  }, []);

  const syncHtmlCodeFromEditor = useCallback(
    () => {
      const editor = editorRef.current;
      if (!editor) return;

      const next = buildPersistedHtmlFromInner(normalizeFormulasForPersistence(editor.getHTML()), "");
      latestHtmlCodeRef.current = next;
      if (htmlModeRef.current) {
        setHtmlCode(next);
      }
    },
    [setHtmlCode]
  );

  const applyPersistedHtmlToEditor = useCallback(
    (rawHtml, { markAsDirty = false } = {}) => {
      const editor = editorRef.current;
      if (!editor) return;

      const raw = rawHtml || "";
      const { contentInner, hasBundle } = parseStyledBundle(raw);

      syncingEditorRef.current = true;
      try {
        const nextRawContent = hasBundle ? contentInner || "<p></p>" : raw || "<p></p>";
        const nextContent = normalizeFormulasForPersistence(nextRawContent);
        editor.commands.setContent(nextContent, false);
      } finally {
        syncingEditorRef.current = false;
      }

      syncToolbarState(editor);

      if (markAsDirty) {
        markDirty();
      }
    },
    [markDirty, syncToolbarState]
  );


  useEffect(() => {
    if (!open) return;

    const editor = editorRef.current;
    if (!editor) return;

    const raw = initialHtml || "";
    const { contentInner, hasBundle } = parseStyledBundle(raw);

    syncingEditorRef.current = true;
    try {
      const nextRawContent = hasBundle ? contentInner || "<p></p>" : raw || "<p></p>";
      const nextContent = normalizeFormulasForPersistence(nextRawContent);
      editor.commands.setContent(nextContent, false);
    } finally {
      syncingEditorRef.current = false;
    }

    latestHtmlCodeRef.current = raw;
    setHtmlCode(raw);
    setHtmlMode(false);
    setDirty(false);

    setFormat("p");
    setColor(COLORS[0].value);
    setFontSize(13);
    setFormulaSelected(false);

    setSaveBusy(false);
    setSaveError("");

    requestAnimationFrame(() => {
      editor.commands.focus("end");
      syncToolbarState(editor);
    });
  }, [open, initialHtml, mode, editorReadyVersion, syncToolbarState]);

  const handleEditorReady = useCallback((editor) => {
    editorRef.current = editor;
    setEditorReadyVersion((prev) => prev + 1);
  }, []);

  const handleEditorUpdate = useCallback(
    (editor) => {
      if (syncingEditorRef.current) return;
      if (htmlModeRef.current) return;

      markDirty();
      syncHtmlCodeFromEditor();
      syncToolbarState(editor);
    },
    [markDirty, syncHtmlCodeFromEditor, syncToolbarState]
  );

  const handleSelectionUpdate = useCallback(
    (editor) => {
      if (syncingEditorRef.current) return;
      syncToolbarState(editor);
    },
    [syncToolbarState]
  );

  const withEditor = useCallback(
    (fn) => {
      const editor = editorRef.current;
      if (!editor || htmlModeRef.current) return false;
      return fn(editor);
    },
    []
  );

  const onFormatChange = useCallback(
    (value) => {
      withEditor((editor) => {
        if (value === "p") {
          editor.chain().focus().setParagraph().run();
        } else {
          const level = Number(value.replace("h", ""));
          if ([1, 2, 3, 4].includes(level)) {
            editor.chain().focus().setHeading({ level }).run();
          }
        }
      });
    },
    [withEditor]
  );

  const onInsertTable = useCallback(() => {
    withEditor((editor) => {
      const rows = parseInt(window.prompt("Anzahl Zeilen:", "2") || "2", 10);
      const cols = parseInt(window.prompt("Anzahl Spalten:", "2") || "2", 10);
      if (!Number.isFinite(rows) || !Number.isFinite(cols)) return;

      const safeRows = Math.max(1, Math.min(12, rows));
      const safeCols = Math.max(1, Math.min(10, cols));
      editor.chain().focus().insertTable({ rows: safeRows, cols: safeCols, withHeaderRow: false }).run();
      markDirty();
      syncHtmlCodeFromEditor();
    });
  }, [markDirty, syncHtmlCodeFromEditor, withEditor]);

  const onHorizontalLine = useCallback(() => {
    withEditor((editor) => {
      editor.chain().focus().setHorizontalRule().run();
      markDirty();
      syncHtmlCodeFromEditor();
    });
  }, [markDirty, syncHtmlCodeFromEditor, withEditor]);

  const onGreyBackground = useCallback(() => {
    withEditor((editor) => {
      editor.chain().focus().toggleGreyBackground().run();
      markDirty();
      syncHtmlCodeFromEditor();
      syncToolbarState(editor);
    });
  }, [markDirty, syncHtmlCodeFromEditor, syncToolbarState, withEditor]);

  const onBold = useCallback(() => {
    withEditor((editor) => {
      editor.chain().focus().toggleBold().run();
      markDirty();
      syncHtmlCodeFromEditor();
    });
  }, [markDirty, syncHtmlCodeFromEditor, withEditor]);

  const onItalic = useCallback(() => {
    withEditor((editor) => {
      editor.chain().focus().toggleItalic().run();
      markDirty();
      syncHtmlCodeFromEditor();
    });
  }, [markDirty, syncHtmlCodeFromEditor, withEditor]);

  const onColor = useCallback(
    (value) => {
      withEditor((editor) => {
        editor.chain().focus().setColor(value).run();
        markDirty();
        syncHtmlCodeFromEditor();
      });
    },
    [markDirty, syncHtmlCodeFromEditor, withEditor]
  );

  const onFontSizeChange = useCallback(
    (value) => {
      if (!Number.isFinite(value)) return;

      withEditor((editor) => {
        editor.chain().focus().setFontSize(value).run();
        markDirty();
        syncHtmlCodeFromEditor();
      });
    },
    [markDirty, syncHtmlCodeFromEditor, withEditor]
  );

  const onBullets = useCallback(() => {
    withEditor((editor) => {
      editor.chain().focus().toggleBulletList().run();
      markDirty();
      syncHtmlCodeFromEditor();
    });
  }, [markDirty, syncHtmlCodeFromEditor, withEditor]);

  const onNumbers = useCallback(() => {
    withEditor((editor) => {
      editor.chain().focus().toggleOrderedList().run();
      markDirty();
      syncHtmlCodeFromEditor();
    });
  }, [markDirty, syncHtmlCodeFromEditor, withEditor]);

  const onSup = useCallback(() => {
    withEditor((editor) => {
      editor.chain().focus().toggleSuperscript().run();
      markDirty();
      syncHtmlCodeFromEditor();
    });
  }, [markDirty, syncHtmlCodeFromEditor, withEditor]);

  const onSub = useCallback(() => {
    withEditor((editor) => {
      editor.chain().focus().toggleSubscript().run();
      markDirty();
      syncHtmlCodeFromEditor();
    });
  }, [markDirty, syncHtmlCodeFromEditor, withEditor]);

  const onLink = useCallback(() => {
    withEditor((editor) => {
      const current = editor.getAttributes("link")?.href || "https://";
      const url = window.prompt("Link einfügen (URL):", current);
      if (url === null) return;

      const trimmed = url.trim();
      if (!trimmed) {
        editor.chain().focus().unsetLink().run();
      } else {
        editor.chain().focus().extendMarkRange("link").setLink({ href: trimmed }).run();
      }

      markDirty();
      syncHtmlCodeFromEditor();
    });
  }, [markDirty, syncHtmlCodeFromEditor, withEditor]);

  const uploadImageFileForEditor = useCallback(async (file) => {
    if (!file || !file.type?.startsWith("image/")) return "";
    if (file.size > MAX_IMAGE_BYTES6) {
      throw new Error("Das Bild ist zu groß. Maximal erlaubt sind 15 MB.");
    }

    setSaveError("");
    try {
      return await uploadImageToCloudinary(file);
    } catch (error) {
      setSaveError(error?.message || "Bild konnte nicht hochgeladen werden.");
      throw error;
    }
  }, []);

  const insertImageFileInEditor = useCallback(
    async (file) => {
      if (!file || !file.type?.startsWith("image/")) return;

      const editor = editorRef.current;
      if (!editor || htmlModeRef.current) return;

      try {
        const src = await uploadImageFileForEditor(file);
        if (!src) return;

        editor
          .chain()
          .focus()
          .insertResizableImage({
            src,
            alt: file.name || "",
            width: DEFAULT_RESIZABLE_IMAGE_WIDTH,
          })
          .run();
        markDirty();
        syncHtmlCodeFromEditor();
      } catch (error) {
        setSaveError(error?.message || "Bild konnte nicht hochgeladen werden.");
      }
    },
    [markDirty, syncHtmlCodeFromEditor, uploadImageFileForEditor]
  );

  const onImageAlign = useCallback(
    (layout) => {
      const safeLayout = ["left", "right", "center"].includes(layout) ? layout : "center";

      withEditor((editor) => {
        const selectedImage = getSelectedImage(editor);
        if (!selectedImage) return;

        editor.chain().focus().updateAttributes("resizableImage", { layout: safeLayout }).run();
        setSelectedImageLayout(safeLayout);
        markDirty();
        syncHtmlCodeFromEditor();
      });
    },
    [markDirty, syncHtmlCodeFromEditor, withEditor]
  );

  const onInsertImage = useCallback(() => {
    if (htmlModeRef.current) return;
    imageInputRef.current?.click();
  }, []);

  const onImageInputChange = useCallback(
    (event) => {
      const files = Array.from(event.target.files || []);
      files.forEach(insertImageFileInEditor);
      event.target.value = "";
    },
    [insertImageFileInEditor]
  );

  const onFormulaToggle = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || htmlModeRef.current) return;

    const selected = getSelectedFormula(editor);
    if (selected) {
      setFormulaSelected(true);
      editor.commands.focus();
      return;
    }

    editor.chain().focus().insertFormula({ latex: "", displayMode: false }).run();
    setFormulaSelected(true);
    markDirty();
    syncHtmlCodeFromEditor();
    syncToolbarState(editor);
  }, [markDirty, syncHtmlCodeFromEditor, syncToolbarState]);

  const onToggleHtmlMode = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    if (!htmlModeRef.current) {
      const source = buildPersistedHtmlFromInner(normalizeFormulasForPersistence(editor.getHTML()), "");
      const formatted = formatHtmlSource6(source);
      latestHtmlCodeRef.current = formatted;
      setHtmlCode(formatted);
      setFormulaSelected(false);
      setHtmlMode(true);
      setTimeout(() => safeFocusTextarea(htmlRef), 0);
      return;
    }

    const source = htmlRef.current?.value ?? latestHtmlCodeRef.current;
    applyPersistedHtmlToEditor(source);
    setHtmlMode(false);
    setTimeout(() => editor.commands.focus("end"), 0);
  }, [applyPersistedHtmlToEditor]);

  const onHtmlCodeChange = useCallback(
    (event) => {
      const next = event.target.value;
      latestHtmlCodeRef.current = next;
      setHtmlCode(next);
      applyPersistedHtmlToEditor(next, { markAsDirty: true });
    },
    [applyPersistedHtmlToEditor]
  );

  const onHtmlCodeKeyDown = useCallback(
    (event) => {
      if (event.key !== "Tab") return;

      event.preventDefault();
      const target = event.currentTarget;
      const start = target.selectionStart ?? 0;
      const end = target.selectionEnd ?? 0;
      const current = target.value ?? latestHtmlCodeRef.current;
      const next = `${current.slice(0, start)}\t${current.slice(end)}`;

      latestHtmlCodeRef.current = next;
      setHtmlCode(next);
      applyPersistedHtmlToEditor(next, { markAsDirty: true });

      setTimeout(() => {
        try {
          target.setSelectionRange(start + 1, start + 1);
        } catch {}
      }, 0);
    },
    [applyPersistedHtmlToEditor]
  );

  const onHtmlScroll = useCallback(() => {
    const pre = htmlPreviewRef.current;
    const ta = htmlRef.current;
    if (!pre || !ta) return;
    pre.scrollTop = ta.scrollTop;
    pre.scrollLeft = ta.scrollLeft;
  }, []);

  const onFormatHtmlClick = useCallback(() => {
    const source = htmlRef.current?.value ?? latestHtmlCodeRef.current;
    const formatted = formatHtmlSource6(source);
    latestHtmlCodeRef.current = formatted;
    setHtmlCode(formatted);
    applyPersistedHtmlToEditor(formatted, { markAsDirty: true });
  }, [applyPersistedHtmlToEditor]);

  const handleSave = useCallback(async () => {
    if (saveBusy) return;

    setSaveError("");
    setSaveBusy(true);

    const editor = editorRef.current;
    const innerRaw = editor?.getHTML() || "";
    const inner = normalizeFormulasForPersistence(normalizeImagesForPersistence(innerRaw));
    const htmlToPersist = buildPersistedHtmlFromInner(inner, "");

    try {
      setFormulaSelected(false);

      const saved = await onSave?.(htmlToPersist);
      if (saved === false) {
        throw new Error("Speichern fehlgeschlagen.");
      }

      setDirty(false);
      onDirtyChange?.(false);
    } catch (error) {
      setSaveError(error?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaveBusy(false);
    }
  }, [onDirtyChange, onSave, saveBusy]);

  const handleRequestClose = useCallback(() => {
    onRequestClose?.();
  }, [onRequestClose]);

  const highlightedHtml = useMemo(() => highlightHtmlSource6(htmlCode), [htmlCode]);

  if (!open) return null;

  const modalTitle =
    title || (mode === "edit" ? "Edit Lesson Content" : "Create Lesson Content");

  return (
    <div className="lh6-editorOverlay" role="dialog" aria-modal="true">
      <div className="lh6-editorBox">
        <header className="lh6-modalHeader">
          <div className="lh6-modalHeaderLeft">
            <div className="lh6-modalIcon">✎</div>
            <div>
              <h2 className="lh6-modalTitle">{modalTitle}</h2>
              <p className="lh6-modalSubtitle">{subtitle}</p>
            </div>
          </div>

          <div className="lh6-modalActions">
            <button type="button" className="lh6-headerBtn ghost" onClick={handleRequestClose}>
              Verwerfen
            </button>
            <button type="button" className="lh6-headerBtn primary" onClick={handleSave} disabled={saveBusy}>
              {saveBusy ? (
                <span className="lh6-saveBusyWrap">
                  <span>Speichern</span>
                  <span className="lh6-saveDots" aria-hidden="true">
                    <span className="lh6-saveDot" />
                    <span className="lh6-saveDot" />
                    <span className="lh6-saveDot" />
                  </span>
                </span>
              ) : (
                "Speichern"
              )}
            </button>
          </div>
        </header>

        <TiptapToolbar
          htmlMode={htmlMode}
          format={format}
          color={color}
          fontSize={fontSize}
          colors={COLORS}
          activeMarks={activeMarks}
          formulaSelected={formulaSelected}
          imageSelected={imageSelected}
          imageLayout={selectedImageLayout}
          onFormatChange={onFormatChange}
          onInsertTable={onInsertTable}
          onHorizontalLine={onHorizontalLine}
          onFontSizeChange={onFontSizeChange}
          onBold={onBold}
          onItalic={onItalic}
          onColor={onColor}
          onBullets={onBullets}
          onNumbers={onNumbers}
          onSup={onSup}
          onSub={onSub}
          onLink={onLink}
          onInsertImage={onInsertImage}
          onImageAlign={onImageAlign}
          onGreyBackground={onGreyBackground}
          onFormulaToggle={onFormulaToggle}
        />

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={onImageInputChange}
        />

        <div className={`lh6-mid ${htmlMode ? "is-html" : ""}`}>
          <TiptapEditor
            htmlMode={htmlMode}
            onEditorReady={handleEditorReady}
            onUpdate={handleEditorUpdate}
            onSelectionUpdate={handleSelectionUpdate}
            onImageFileUpload={uploadImageFileForEditor}
          />

          <div className={`lh6-htmlPane ${htmlMode ? "is-visible" : "is-hidden"}`}>
            <div className="lh6-htmlWrap">
              <pre
                ref={htmlPreviewRef}
                className="lh6-htmlPreview"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: `${highlightedHtml}${htmlCode.endsWith("\n") ? "" : "\n"}` }}
              />
              <textarea
                ref={htmlRef}
                className="lh6-htmlArea"
                value={htmlCode}
                onChange={onHtmlCodeChange}
                onKeyDown={onHtmlCodeKeyDown}
                onScroll={onHtmlScroll}
                spellCheck={false}
                placeholder="HTML-Quelltext..."
              />
            </div>
          </div>
        </div>

        <footer className="lh6-footer">
          <div className="lh6-footerTop">
            <TiptapHtmlToggle htmlMode={htmlMode} onToggle={onToggleHtmlMode} onFormatHtml={onFormatHtmlClick} />
          </div>

          {saveError ? <div className="lh6-editorError">{saveError}</div> : null}
        </footer>
      </div>
    </div>
  );
}
